/** What people read when something goes wrong. Written for customers and staff, not developers. */
export const ERROR_MESSAGES = {
  badRequest: "That request couldn't be processed.",
  validationFailed: "Please fix the highlighted fields.",
  unauthorized: "Please sign in to continue.",
  sessionExpired: "Your session has ended. Sign in again.",
  forbidden: "You don't have access to this.",
  notFound: (entity: string) => `${entity} not found`,
  rateLimited: "Too many tries. Please wait a minute and try again.",
  internalError: "Something went wrong on our side. Please try again.",
  serviceUnavailable: "We're having trouble reaching our servers. Please try again in a minute.",
  busy: "Lots happening at once — please try that again.",
  duplicate: (field: string) => `That ${field} is already in use.`,
  inUse: "Other records still depend on this. Remove those first.",
  conflict: "That change no longer fits the current data. Refresh and try again.",
  timeout: "That took too long. Please try again.",
  /** The apps' API clients: no connection, or no answer in time. */
  network: "Can't reach Milqit right now. Check your connection and try again.",
  noAnswer: "Milqit is taking too long to answer. Try again.",
  /** Bare HTTP errors the framework raises before our code runs. */
  pageNotFound: "We couldn't find that. Check the address and try again.",
  methodNotAllowed: "That action isn't available here.",
  payloadTooLarge: "That's too much to send at once. Try something smaller.",
  unsupportedMediaType: "We couldn't read that request. Please try again.",
  invalidJson: "We couldn't read that request. Please try again.",
  requestFailed: "Request failed",

  /** Limits that stop someone guessing codes or passwords. */
  signIn: {
    codesThisHour: "You've asked for several codes in the last hour. Try again later.",
    codesToday: "You've asked for too many codes today. Try again tomorrow or call us.",
    codesFromNetwork: "Lots of codes have been asked for from this network. Try again later.",
    codeLocked: "Too many wrong codes for this number. Try again in an hour.",
    staffLocked: "Too many sign-in attempts for this account. Wait 15 minutes and try again.",
  },

  order: {
    placeFailed: "We couldn't place your order",
    belowMinimum: (minimum: number, short: number) =>
      `Add ₹${short.toLocaleString("en-IN")} more — the minimum order is ₹${minimum.toLocaleString("en-IN")}.`,
    outsideBookingWindow: "Choose a delivery morning within the next week.",
    closedForTomorrow: (cutoff: string) =>
      `Orders for tomorrow closed at ${cutoff}. Choose the morning after or later.`,
    cancelClosed: (cutoff: string) =>
      `This order can no longer be cancelled — cancelling closes at ${cutoff} the night before delivery. Contact our care team for help.`,
  },

  /** Why a basket line can't be ordered as it is (see CART_LINE_PROBLEMS). */
  cart: {
    unavailable: "No longer sold — remove it to place your order.",
    sizeUnavailable: "That size isn't sold any more — remove it and pick another.",
    outOfStock: "Sold out for now — remove it to place your order.",
    shortStock: (available: number) => `Only ${available} left — reduce it to place your order.`,
    notSubscribable: (name: string) => `${name} can't go on a plan — buy it once instead.`,
    comingSoon: (name: string) =>
      `${name} isn't on sale yet — remove it to place your order. We'll have it soon.`,
  },

  slot: {
    unavailable: "That delivery slot isn't available that day. Please pick another.",
    full: "That delivery slot just filled up. Please pick another.",
    taken: (label: string) => `This hub already has a ${label} slot.`,
    inUse: (count: number) =>
      `${count} upcoming ${count === 1 ? "delivery is" : "deliveries are"} booked in this slot. Close it instead, or add a new slot.`,
    notServiced: "No hub delivers to this pincode yet.",
    noRiders: "Deliveries to this area haven't started yet. Please check back soon.",
    addressNotServiced: (pincode: string) =>
      `We don't deliver to ${pincode} yet. Please use an address in an area we serve.`,
  },

  location: {
    unavailable: "Address search isn't available right now. Please type your address.",
    notFound: "We couldn't find an address there. Please type it in.",
    permissionDenied:
      "Location access is off for this site. Allow it in your browser, or type your address.",
    unsupported: "This browser can't share your location. Please type your address.",
    timeout: "Finding your location took too long. Try again, or type your address.",
    notServed: (area: string) =>
      `We don't deliver to ${area} yet — Milqit is in Nagpur (pincodes 4400xx).`,
  },

  invoice: {
    notReady: (orderNumber: string) =>
      `${orderNumber} hasn't been delivered yet. Its tax invoice is issued on delivery.`,
    generateFailed: "Couldn't generate invoices",
  },

  payment: {
    unavailable:
      "Online payment isn't available right now. Choose cash on delivery or try again shortly.",
    notRequired: "This order doesn't need an online payment.",
    codUnavailable: "Cash on delivery isn't available right now. Please pay online.",
    codLimit: (limit: number) =>
      `Cash on delivery is for orders up to ₹${limit.toLocaleString("en-IN")}. Please pay online.`,
    pendingConfirm: "This order is waiting for its online payment, so it can't be confirmed yet.",
    signatureInvalid:
      "We couldn't confirm that payment. If money left your account, it will be refunded automatically.",
    mismatch: "That payment belongs to a different order.",
    expired: "This order was cancelled because it wasn't paid in time. Please place it again.",
    notCompleted: "Payment not completed",
    notCompletedHint: "Your order is saved for 30 minutes — pay now or choose cash on delivery.",
    failed: "Payment didn't go through",
    scriptFailed: "We couldn't open the payment window. Check your connection and try again.",
    unpaidCancelled: "Not paid within the time limit",
    webhookSignatureInvalid: "Webhook signature doesn't match.",
    webhookNotConfigured: "Razorpay webhooks aren't set up on this server.",
  },

  coupon: {
    invalid: "That code isn't valid. Check the spelling and try again.",
    notActive: (code: string) => `${code} isn't available right now.`,
    scheduled: (code: string) => `${code} isn't live yet — try again soon.`,
    expired: (code: string) => `${code} has expired.`,
    usedUp: (code: string) => `${code} has been fully claimed.`,
    firstOrder: (code: string) => `${code} is for your first order only.`,
    limitReached: (code: string, limit: number) =>
      limit === 1
        ? `You've already used ${code}.`
        : `You've used ${code} ${limit} times — that's the limit.`,
    minSubtotal: (code: string, shortBy: number) =>
      `Add items worth ₹${shortBy} more to use ${code}.`,
    noSaving: (code: string) => `${code} doesn't lower this order — delivery is already free.`,
    signIn: "Sign in to use a coupon — offers depend on your account.",
    notEligible: (code: string, aisles: string) =>
      `${code} is for ${aisles} — add something from there to use it.`,
    checkFailed: "We couldn't check that code. Check your connection and try again.",
    removedAtCheckout: "Coupon removed",

    duplicateCode: (code: string) => `Code ${code} already exists.`,
    codeLocked: (code: string) =>
      `${code} has already been used, so its code can't change. Duplicate it instead.`,
    usageLimitTooLow: (code: string, used: number) =>
      `${code} has been used ${used} times — set the limit to at least that.`,
    cannotResumeExpired: "Expired coupons can't be resumed — extend the end date instead.",
    cannotDeleteUsed: (code: string, used: number) =>
      `${code} has been used ${used} times — pause it instead.`,
    saveFailed: "Couldn’t save the coupon",
    updateFailed: "Couldn’t update the coupon",
    deleteFailed: "Couldn’t delete the coupon",
    deleteUsedTitle: "Can’t delete a coupon that’s been used",
    copyFailed: "Couldn’t copy — select the code instead",
  },
} as const;
